// "Why am I seeing these incidents?" — the note under the incidents list.
//
// Incident visibility has three tiers and two axes (a global role, or an
// attendance role on one event) and the answer is rarely obvious from the list
// itself: an empty table looks the same whether the event has no incidents, your
// attendance has lapsed, or a filter is hiding them. This says which it is.
//
// Everything here is derived from the capability matrix `/me` serves — the same
// one core/permissions.py enforces from — rather than from a copy of the rules, so
// it follows a change to the matrix automatically. The one thing it must keep in
// step by hand is the *shape* of the tiers in core/authz.incident_visibility():
// global viewAll ⇒ everything, an event's viewAll role ⇒ that whole event,
// any other active role ⇒ your own incidents (including any shared with you) plus
// same-participant discovery.
import { Alert, AlertTitle, Stack, Typography } from "@mui/material";
import { ATTENDANCE_GRACE_DAYS, INCIDENT_SCOPES, INCIDENT_SCOPE_ALL } from "../constants";
import { useMe } from "../MeContext";

// "A", "A and B", "A, B and C" — global role names read as prose here.
const roleList = (roles) => {
  if (roles.length <= 1) return roles[0] || "";
  return `${roles.slice(0, -1).join(", ")} and ${roles[roles.length - 1]}`;
};

const Line = ({ children }) => <Typography variant="body2">{children}</Typography>;

/**
 * Props:
 *  - eventId, eventLabel   the event the list is filtered to (null when none is)
 *  - eventInWindow         whether that event is still within the attendance grace
 *                          window. Needed because `useMe().can()` deliberately
 *                          knows nothing about the window, so an event-scoped
 *                          capability reads true for an event the server has
 *                          already closed off — and this note must not promise
 *                          incidents that won't arrive.
 *  - userMode              the User view, whose event picker is limited to their own
 *  - scope                 the "Show" filter's value (INCIDENT_SCOPES)
 */
export default function IncidentVisibilityNote({
  eventId, eventLabel, eventInWindow = true, userMode, scope,
}) {
  const { me, can } = useMe();
  if (!me) return null;

  const scopeOption = INCIDENT_SCOPES.find(o => o.value === scope);
  const scopeLabel = scopeOption?.label || "";
  // The option's own wording, lower-cased to read on from "narrowed further to".
  const scopeHelp = (scopeOption?.help || "").charAt(0).toLowerCase()
    + (scopeOption?.help || "").slice(1);

  // The global tier, checked exactly as authz.incident_visibility does first: the
  // global-context capability, with no event in scope.
  const seeAll = can("Incidents.viewAll");
  // Which role is responsible. Super Admin isn't in the matrix (can() short-circuits
  // on it), and it's the stronger reason when someone holds both; otherwise read the
  // granting roles off the matrix so this still names the right one if it changes.
  const granting = me.is_super
    ? ["Super Admin"]
    : (me.global_roles || []).filter(r =>
        (me.matrix?.permissions?.["Incidents.viewAll"]?.global || []).includes(r));

  // Their attendance on the event in scope. Object keys arrive as strings.
  const att = eventId != null
    ? (me.event_roles?.[eventId] || me.event_roles?.[String(eventId)] || null)
    : null;
  const eventName = <strong>{eventLabel || "this event"}</strong>;

  // The two event-scoped tiers, read from the attendance role against the matrix's
  // *event* column rather than through `can()`. That's deliberate: `can()` unions
  // the global column in, but incident_visibility only ever consults the global
  // column for Incidents.viewAll — so a global role granting plain Incidents.view
  // would genuinely see nothing, and `can()` would say otherwise.
  const eventRole = att?.active && eventInWindow ? att.role : null;
  const grantsOnEvent = (perm) =>
    !!eventRole && (me.matrix?.permissions?.[perm]?.event || []).includes(eventRole);
  const eventViewAll = grantsOnEvent("Incidents.viewAll");
  const eventAny = grantsOnEvent("Incidents.view");

  const basis = () => {
    if (seeAll) {
      return (
        <Line>
          You have the <strong>{roleList(granting)}</strong>{" "}
          global role, so you can see <strong>every incident on every event</strong>,
          with no time limit.
        </Line>
      );
    }
    if (eventViewAll) {
      return (
        <Line>
          You have the <strong>{att?.role}</strong> role on {eventName}, so you can
          see <strong>every incident on this event</strong>.
        </Line>
      );
    }
    if (eventAny) {
      return (
        <Line>
          You have the <strong>{att?.role}</strong> role on {eventName}, so you can
          see <strong>only</strong> incidents you created, are allocated (personally
          or through one of your teams), have written a note on, or have had shared
          with you — plus any other incident on this event for the same participant
          as one of those.
        </Line>
      );
    }
    // No tier applies. Say which of the several reasons it is, since they lead to
    // quite different next steps.
    if (!me.linked) {
      return (
        <Line>
          Your sign-in isn't linked to a Personnel record, so no incidents are
          available to you.
        </Line>
      );
    }
    if (eventId == null) {
      return <Line>Choose an event above to see what you can access on it.</Line>;
    }
    // No branch for an inactive attendance ("Available", or no status): such an
    // event never reaches the picker, because it can't be selected on the Incidents
    // page any more than it's listed on My events. So it can't be the event in
    // scope here.
    if (att?.active && !eventInWindow) {
      return (
        <Line>
          Your <strong>{att.role}</strong> role on {eventName} has lapsed: an event
          role stops granting access {ATTENDANCE_GRACE_DAYS} days after the event
          ends, so none of its incidents are listed.
        </Line>
      );
    }
    return (
      <Line>
        You have no role on {eventName} that covers incidents, and no global role
        that does, so none of its incidents are listed.
      </Line>
    );
  };

  // Attendance-derived access expires; a global role never does. Only worth saying
  // to someone actually relying on the attendance.
  const expires = !seeAll && (eventViewAll || eventAny);

  return (
    <Alert severity="info" variant="outlined">
      <AlertTitle>What you can see</AlertTitle>
      <Stack spacing={0.75}>
        {basis()}

        {expires && (
          <Line>
            That access comes from being on the event, so it ends{" "}
            {ATTENDANCE_GRACE_DAYS} days after the event finishes.
          </Line>
        )}

        {userMode && (
          <Line>
            {seeAll
              ? "This view lists one event at a time. Your global role covers every "
                + "event, so all of them are offered."
              : "This view lists one event at a time, and offers only events you have "
                + "been selected for — being flagged Available isn't enough."}
          </Line>
        )}

        {/* The Show filter narrows what's on screen below what the rules allow, so
            what it's set to is part of the answer — and the User view opens on a
            narrowed one. */}
        <Line>
          {scope === INCIDENT_SCOPE_ALL ? (
            <>
              <strong>Show</strong> is set to <strong>{scopeLabel}</strong>, so the
              list holds everything described above.
            </>
          ) : (
            <>
              <strong>Show</strong> is set to <strong>{scopeLabel}</strong>, so the
              list is narrowed further to {scopeHelp} Choose{" "}
              <strong>All visible to me</strong> to see everything described above.
            </>
          )}
        </Line>
      </Stack>
    </Alert>
  );
}
